Woolman.Utils.prepareQueryParams();
Woolman.Collection = document.querySelector('[data-ajax-parent]');

// Price sorting must be done client-side: Shopify sorts collections by the
// catalog price used for sorting, which on this store does not match the price
// shown to the customer (Markets/catalog pricing). As a result Shopify returns
// the manual order for price-ascending/price-descending. 
const PRICE_SORT_VALUES = ['price-ascending', 'price-descending'];

const isPriceSort = () =>
  !!(Shopify.queryParams && PRICE_SORT_VALUES.includes(Shopify.queryParams.sort_by));

const hasPriceFilter = () =>
  ['filter.v.price.gte', 'filter.v.price.lte'].some((key) =>
    Shopify.queryParams[key] !== undefined && Shopify.queryParams[key] !== '');

const needsClientPrices = () => isPriceSort() || hasPriceFilter();

// Set synchronously so infinite-scroll.js can opt out before it initialises.
window.__priceSortActive = needsClientPrices();

/**
 * Generates search params from form data for filtering collection or search results.
 * @param {HTMLElement} formElement - Form element to turn into search params.
 * @returns
 */
const generateSearchParamsFromFormData = (formElement) => {
  const checkboxesToKeep = {};
  for (const element of formElement.elements) {
    const key = element.getAttribute('name');
    if (!key || !key.includes('filter')) continue;

    const value = element.value;
    const inputType = element.getAttribute('type');

    if (value === '') continue;

    if (inputType == 'number' && (element.hasAttribute('data-min') || element.hasAttribute('data-max'))) {
      const priceSlider = element.closest('price-range-slider');
      if (!priceSlider || !priceSlider.hasAttribute('data-updated')) {
        continue; // Skip if slider hasn't been updated by user
      }
    }

    if (inputType == 'checkbox') {
      if (element.checked) {
        if (!checkboxesToKeep[key]) {
          checkboxesToKeep[key] = [value];
          continue;
        } else {
          checkboxesToKeep[key].push(value);
          continue;
        }
      }

      if (Shopify.queryParams[key] && !checkboxesToKeep[key]) {
        delete Shopify.queryParams[key];
      }
      continue;
    } else {
      Shopify.queryParams[key] = value;
    }
  }

  if (Object.entries(checkboxesToKeep).length > 0) {
    for (const [key, value] of Object.entries(checkboxesToKeep)) {
      Shopify.queryParams[key] = value.join(',');
    }
  }

  return Shopify.queryParams;
};

/**
 * Syncs checkbox states between all facets-form instances
 * Ensures sidebar and drawer forms stay in sync
 * @param {HTMLElement} changedCheckbox - The checkbox that was changed (optional)
 */
const syncCheckboxStates = (changedCheckbox = null) => {
  const allFacetsForms = document.querySelectorAll('facets-form');

  // If a specific checkbox changed, sync only that one
  if (changedCheckbox) {
    const name = changedCheckbox.getAttribute('name');
    const value = changedCheckbox.value;
    const checked = changedCheckbox.checked;

    if (name && name.includes('filter')) {
      allFacetsForms.forEach(form => {
        const correspondingInput = form.querySelector(`input[name="${name}"][value="${value}"]`);
        if (correspondingInput && correspondingInput !== changedCheckbox) {
          correspondingInput.checked = checked;
        }
      });
    }
    return;
  }

  // Otherwise, sync all checkboxes
  const allCheckboxes = document.querySelectorAll('facets-form input[type="checkbox"]');
  allCheckboxes.forEach(checkbox => {
    const name = checkbox.getAttribute('name');
    const value = checkbox.value;

    if (!name || !name.includes('filter')) return;

    // Find all corresponding checkboxes in other forms
    allFacetsForms.forEach(form => {
      const correspondingInput = form.querySelector(`input[name="${name}"][value="${value}"]`);
      if (correspondingInput && correspondingInput !== checkbox) {
        correspondingInput.checked = checkbox.checked;
      }
    });
  });
};



/**
 * This function fetches fresh collection page via AJAX request to collection's url.
 * @param {Array} selectors - Array of HTML element selectors to update.
 */
const getCollectionAJAX = async (selectors) => {
  Shopify.queryParams.page = 1;
  // Keep this in sync before any markup is rendered so a freshly inserted
  // <infinite-scroll> element knows whether to stand down for the price sort.
  window.__priceSortActive = needsClientPrices();
  const CollectionURLSearchParams = getCollectionURLSearchParams(),
        url = location.pathname + '?' + CollectionURLSearchParams;

  if (selectors) {
    try {
      const res = await fetch(url, {
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      const body = await res.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(body, 'text/html');

      for (const selector of selectors) {
        if (!document.querySelector(selector) || !doc.querySelector(selector)) continue;

        document.querySelector(selector).innerHTML = doc.querySelector(selector).innerHTML;

        // Update apply number
        if (selector === '#product-grid-ajax') {
          let filteredCount = parseInt(doc.querySelector(selector).dataset.filteredCount, 10),
              context = doc.querySelector(selector).dataset.context,
              zeroResultsMsg = document.getElementById('facets-no-results-message'),
              resultsStringOne = window.themeStrings.product_count_simple_one,
              resultsStringOther = window.themeStrings.product_count_simple_other;

          if (context === 'search') {
            resultsStringOne = window.themeStrings.results_with_count_one;
            resultsStringOther = window.themeStrings.results_with_count_other;
          }

          if (filteredCount === 0) {
            document.getElementById('apply-count').innerHTML = resultsStringOther.replace('[count]', filteredCount);
            zeroResultsMsg.classList.remove('hide');
          } else if (filteredCount === 1) {
            document.getElementById('apply-count').innerHTML = resultsStringOne.replace('[count]', filteredCount);
            zeroResultsMsg.classList.add('hide');
          } else {
            document.getElementById('apply-count').innerHTML = resultsStringOther.replace('[count]', filteredCount);
            zeroResultsMsg.classList.add('hide');
          }
        }
      }

      window.history.replaceState('', '', url);

      if (needsClientPrices()) {
        // Re-sort the whole result set by displayed price and take over pagination.
        await applyGlobalPriceSort();
      } else {
        window.__priceSortActive = false;
        Woolman.Collection.classList.remove('processing');
        await applyGlobalPriceSort({ rangeOnly: true });
      }

    } catch (err) {
      location.search = CollectionURLSearchParams;
    }
  } else {
    location.search = CollectionURLSearchParams;
  }
};

/**
 * Returns URL for current collection. Utilizes URL parameters to save user's filters, sorting and pagination.
 * @returns
 */
const getCollectionURLSearchParams = () => {
  return new URLSearchParams(Object.entries(Shopify.queryParams)).toString().replaceAll(/%2C/g, ',');
};

// Holds the globally sorted product cards and how many are currently shown.
let priceSortState = null;

/**
 * Parses a localized price string (e.g. "$44.99", "1.299,00 €", "From $9.99")
 * into a numeric value for comparison.
 * @param {string} priceString
 * @returns {number}
 */
const parsePrice = (priceString) => {
  if (!priceString) return 0;
  let s = priceString.replace(/[^0-9.,]/g, '').trim();
  if (!s) return 0;

  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');

  if (lastComma > -1 && lastDot > -1) {
    // Whichever separator comes last is the decimal separator.
    s = lastComma > lastDot ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  } else if (lastComma > -1) {
    // Only commas present: treat as decimal when 1-2 trailing digits, else thousands.
    const decimals = s.length - lastComma - 1;
    s = decimals === 1 || decimals === 2 ? s.replace(',', '.') : s.replace(/,/g, '');
  }

  return parseFloat(s) || 0;
};

/**
 * Reads the price actually displayed on a product card (the current selling
 * price, including any theme-applied discount), ignoring the compare-at price.
 * @param {Element} card
 * @returns {number}
 */
const getCardPrice = (card) => {
  const price = card.querySelector('.price[data-price]');
  if (price) return Number(price.dataset.price) / 100;
  const el = card.querySelector('.price__regular .price-item--regular') ||
             card.querySelector('.price-item--regular');
  return parsePrice(el ? el.textContent : '');
};

/**
 * Builds the current collection/search query params without the page/view keys,
 * which are managed per request by the price sort.
 * @returns {URLSearchParams}
 */
const getBaseParamsWithoutPage = () => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(Shopify.queryParams)) {
    if (key === 'page' || key === 'view' || key === 'filter.v.price.gte' || key === 'filter.v.price.lte') continue;
    params.set(key, value);
  }
  return params;
};

// Spinner markup matching the theme's `loading` snippet.
const SPINNER_MARKUP =
  '<div class="loading-overlay__spinner">' +
    '<svg aria-hidden="true" focusable="false" role="presentation" style="width: 15px; height: 15px;" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
      '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>' +
    '</svg>' +
  '</div>';

const PRICE_SORT_SPINNER = `<div class="price-sort-loading"><div class="loading-overlay">${SPINNER_MARKUP}</div></div>`;

/**
 * Hides the (still unsorted) grid and shows a spinner until the sort is applied.
 * @param {Element} grid
 */
const showPriceSortLoading = (grid) => {
  grid.classList.add('price-sort-pending');
  if (!grid.querySelector('.price-sort-loading')) {
    grid.insertAdjacentHTML('afterbegin', PRICE_SORT_SPINNER);
  }
};

/**
 * Reveals the grid again and removes the spinner.
 * @param {Element} grid
 */
const hidePriceSortLoading = (grid) => {
  grid.classList.remove('price-sort-pending');
  const loader = grid.querySelector('.price-sort-loading');
  if (loader) loader.remove();
};

/**
 * Fetches every page of the current (filtered) collection, sorts all product
 * cards by their displayed price and renders the first page client-side.
 */
const applyGlobalPriceSort = async ({ rangeOnly = false } = {}) => {
  const grid = document.getElementById('product-grid-ajax');
  if (!grid) return;
  if (rangeOnly && !document.querySelector('price-range-slider')) return;

  if (!rangeOnly) {
    window.__priceSortActive = true;
    Woolman.Collection && Woolman.Collection.classList.add('processing');

    // Hide the unsorted grid immediately (also covers runtime sort/filter changes,
    // where the server has just rendered the manual order) so nothing flashes.
    showPriceSortLoading(grid);
  }

  const ascending = Shopify.queryParams.sort_by === 'price-ascending';

  // Work out the page size and number of pages from the rendered grid.
  const filteredCount = parseInt(grid.dataset.filteredCount, 10) || 0;

  // Nothing to sort: leave the server-rendered empty state in place.
  if (filteredCount === 0 && !hasPriceFilter()) {
    if (rangeOnly) return;
    hidePriceSortLoading(grid);
    priceSortState = null;
    Woolman.Collection && Woolman.Collection.classList.remove('processing');
    return;
  }

  // How many cards to reveal per "Load more" click (keeps the on-screen page size).
  const displayBatchSize = parseInt(grid.dataset.pageSize, 10) || 24;

  // Collections use a dedicated view that paginates by the Shopify maximum so the
  // whole result set is fetched in as few requests as possible. Search has no such
  // view and falls back to its normal page size.
  const useBulkView = grid.dataset.context !== 'search';
  const base = getBaseParamsWithoutPage();
  const parser = new DOMParser();
  const cards = [];
  const seen = new Set();
  let candidateCount = filteredCount;

  const buildUrl = (page) => {
    const params = new URLSearchParams(base.toString());
    params.set('page', page);
    if (useBulkView) params.set('view', 'pricesort');
    return location.pathname + '?' + params.toString().replaceAll(/%2C/g, ',');
  };

  const fetchPage = (page) =>
    fetch(buildUrl(page), { headers: { 'Cache-Control': 'no-cache' } })
      .then((res) => res.text())
      .catch(() => '');

  const collectCards = (html) => {
    if (!html) return 0;
    const fetchedGrid = parser.parseFromString(html, 'text/html').getElementById('product-grid-ajax');
    if (!fetchedGrid) return 0;

    candidateCount = parseInt(fetchedGrid.dataset.filteredCount, 10) || 0;
    const found = fetchedGrid.querySelectorAll('.card--product');
    found.forEach((card) => {
      const link = card.querySelector('a[href*="/products/"]');
      const key = link ? link.getAttribute('href').split('?')[0] : `idx-${cards.length}`;
      if (seen.has(key)) return;
      seen.add(key);
      cards.push(card);
    });
    return found.length;
  };

  // Fetch page 1 first to discover the real per-page size (the platform may cap
  // it below the requested maximum), then fetch any remaining pages in parallel.
  const firstPageSize = collectCards(await fetchPage(1));
  const totalPages = firstPageSize > 0 ? Math.min(Math.ceil(candidateCount / firstPageSize), 200) : 1;

  if (totalPages > 1) {
    const rest = [];
    for (let page = 2; page <= totalPages; page++) rest.push(fetchPage(page));
    (await Promise.all(rest)).forEach(collectCards);
  }

  // If every fetch failed, keep the server-rendered list rather than blanking it.
  if (cards.length < candidateCount) {
    if (rangeOnly) return;
    hidePriceSortLoading(grid);
    Woolman.Collection && Woolman.Collection.classList.remove('processing');
    return;
  }

  if (cards.length > 0 && base.toString() === getBaseParamsWithoutPage().toString()) {
    const maximum = Math.ceil(cards.reduce((highest, card) => Math.max(highest, getCardPrice(card)), 0));
    const maxValue = Shopify.queryParams['filter.v.price.lte'];
    document.querySelectorAll('price-range-slider').forEach((slider) =>
      slider.setMaximum(maximum, maxValue !== undefined && maxValue !== ''));
  }
  if (rangeOnly) return;

  const minPrice = Number(Shopify.queryParams['filter.v.price.gte'] || 0);
  const maxPrice = Shopify.queryParams['filter.v.price.lte'];
  const matchingCards = hasPriceFilter() ? cards.filter((card) => {
    const price = getCardPrice(card);
    return price >= minPrice && (maxPrice === undefined || maxPrice === '' || price <= Number(maxPrice));
  }) : cards;

  if (isPriceSort()) {
    matchingCards.sort((firstCard, secondCard) => {
      const difference = getCardPrice(firstCard) - getCardPrice(secondCard);
      return ascending ? difference : -difference;
    });
  }

  priceSortState = { cards: matchingCards, shown: 0, pageSize: displayBatchSize };

  if (hasPriceFilter()) {
    const count = matchingCards.length;
    const strings = window.themeStrings;
    const isSearch = grid.dataset.context === 'search';
    const countString = count === 1
      ? (isSearch ? strings.results_with_count_one : strings.product_count_simple_one)
      : (isSearch ? strings.results_with_count_other : strings.product_count_simple_other);
    const countText = countString.replace('[count]', count);
    grid.dataset.filteredCount = count;
    document.querySelectorAll('#products-count-ajax, #apply-count').forEach((element) => {
      element.textContent = countText;
    });
    const emptyMessage = document.getElementById('facets-no-results-message');
    if (emptyMessage) emptyMessage.classList.toggle('hide', count !== 0);
  }

  grid.innerHTML = '';
  if (matchingCards.length === 0) {
    const message = document.getElementById('facets-no-results-message');
    if (message) {
      const emptyState = document.createElement('p');
      emptyState.textContent = message.textContent;
      grid.appendChild(emptyState);
    }
  }
  renderNextPriceSortBatch();
  hidePriceSortLoading(grid);

  Woolman.Collection && Woolman.Collection.classList.remove('processing');
};

/**
 * Appends the next batch of sorted cards to the grid and refreshes the
 * client-side "Load more" control.
 */
const renderNextPriceSortBatch = () => {
  const grid = document.getElementById('product-grid-ajax');
  if (!grid || !priceSortState) return;

  const { cards, shown, pageSize } = priceSortState;
  const batch = cards.slice(shown, shown + pageSize);
  const fragment = document.createDocumentFragment();
  batch.forEach((card) => fragment.appendChild(card));
  grid.appendChild(fragment);
  priceSortState.shown += batch.length;

  renderPriceSortPagination();
};

/**
 * Renders a client-side "Load more" button (or the end-of-results message)
 * for the price-sorted view, replacing Shopify's server pagination.
 */
const renderPriceSortPagination = () => {
  const paginationEl = document.getElementById('pagination-ajax');
  if (!paginationEl || !priceSortState) return;

  const { cards, shown } = priceSortState;
  const strings = window.themeStrings || {};

  if (shown < cards.length) {
    // Mirror the standard Load more markup from snippets/pagination.liquid.
    paginationEl.innerHTML =
      '<div class="pagination mt-48 mb-48">' +
        '<button type="button" class="btn btn--secondary size--small js-price-sort-load-more">' +
          `<span>${strings.load_more || 'Load more'}</span>` +
          `<div class="loading-overlay hidden">${SPINNER_MARKUP}</div>` +
        '</button>' +
      '</div>';
    paginationEl.querySelector('.js-price-sort-load-more')
      .addEventListener('click', renderNextPriceSortBatch);
  } else {
    paginationEl.innerHTML =
      '<div class="pagination mt-48 mb-48">' +
        `<p class="end-of-items style--subdued size--tiny">${strings.all_loaded || ''}</p>` +
      '</div>';
  }
};

class SortBySelect extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.select = this.querySelector('select');
    this.radios = this.querySelectorAll('input[type="radio"]');
    this.selectors = {
      grid: '#product-grid-ajax',
      pagination: '#pagination-ajax',
    };
    this.isSyncing = false; // Flag to prevent change events during sync

    if (Shopify.queryParams['sort_by']) {
      if (this.select) {
        this.select.value = Shopify.queryParams['sort_by'];
      } else if (this.radios.length > 0) {
        this.radios.forEach(radio => {
          if (radio.value === Shopify.queryParams['sort_by']) {
            radio.checked = true;
          }
        });
      }
    }

    if (this.select) {
      this.select.addEventListener('change', this.onSelectChange.bind(this));
      this.adjustSelectWidth(); // Set initial width
    } else if (this.radios.length > 0) {
      this.radios.forEach(radio => {
        radio.addEventListener('change', this.onSelectChange.bind(this));
      });
    }
  }

  /**
   * Adjusts the width of the select element to fit the currently selected option
   */
  adjustSelectWidth() {
    if (!this.select) return;

    const selectedOption = this.select.options[this.select.selectedIndex];
    if (!selectedOption) return;

    // Create a temporary span to measure text width
    const tempSpan = document.createElement('span');
    const computedStyle = getComputedStyle(this.select);
    tempSpan.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: nowrap;
      font-family: ${computedStyle.fontFamily};
      font-size: ${computedStyle.fontSize};
      font-weight: ${computedStyle.fontWeight};
      letter-spacing: ${computedStyle.letterSpacing};
    `;
    tempSpan.textContent = selectedOption.textContent;
    document.body.appendChild(tempSpan);
    
    const textWidth = tempSpan.offsetWidth;
    document.body.removeChild(tempSpan);
    
    // Add padding for the icon and buffer space (icon area is 4.2rem on the right)
    const paddingRight = parseFloat(computedStyle.paddingRight) || 42;
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 12;
    
    // Calculate width with some extra buffer for comfort (8px)
    const newWidth = textWidth + paddingRight + paddingLeft + 8;
    this.select.style.width = `${newWidth}px`;
  }

  connectedCallback() {
    // Initial sync is handled by the constructor setting values from query params
  }

  /**
   * Syncs the value of this sort-by element to all other sort-by instances
   * @param {string} value - The value to sync to all instances
   */
  syncToAllInstances(value) {
    if (this.isSyncing) return; // Prevent infinite loops

    this.isSyncing = true;
    const allSortByElements = document.querySelectorAll('sort-by');

    allSortByElements.forEach((sortByElement) => {
      if (sortByElement === this) return; // Skip self

      sortByElement.isSyncing = true; // Set flag on target element

      // Handle select elements
      if (sortByElement.select) {
        if (sortByElement.select.value !== value) {
          sortByElement.select.value = value;
          sortByElement.adjustSelectWidth(); // Adjust width after value change
        }
      }
      // Handle radio buttons
      else if (sortByElement.radios.length > 0) {
        sortByElement.radios.forEach(radio => {
          radio.checked = (radio.value === value);
        });
      }

      sortByElement.isSyncing = false; // Clear flag on target element
    });

    this.isSyncing = false;
  }

  async onSelectChange(event) {
    // Don't trigger AJAX if this change was caused by syncing
    if (this.isSyncing) {
      return;
    }

    const value = event.target.value;

    // Adjust width for the current select
    this.adjustSelectWidth();

    // Sync to all other sort-by instances (without triggering their change events)
    this.syncToAllInstances(value);

    // Now trigger the AJAX update
    Woolman.Collection.classList.add('processing');
    Woolman.Utils.prepareQueryParams();
    Shopify.queryParams.sort_by = value;
    getCollectionAJAX(Object.values(this.selectors));
  }
}

if (customElements.get('sort-by') === undefined) {
  customElements.define('sort-by', SortBySelect);
}

class FacetsForm extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.el = this.querySelector('form');
    this.selectors = {
      grid: '#product-grid-ajax',
      count: '#products-count-ajax',
      currentFacets: '#current-facets-ajax',
      pagination: '#pagination-ajax',
    };

    this.el.addEventListener('submit', this.onFormSubmit.bind(this));
  }

  connectedCallback() {
    // Set up input change listeners and sync functionality
    const inputs = this.el.querySelectorAll('input');

    inputs.forEach((input) => {
      // Create debounced AJAX handler for this input
      const debouncedAJAX = debounce(this.onInputChange.bind(this), 150);

      input.addEventListener('change', (event) => {
        if (input.type === 'checkbox') {
          // Sync immediately on change, before AJAX
          syncCheckboxStates(input);
        }
        // Then trigger debounced AJAX update
        debouncedAJAX(event);
      });
    });
  }

  async onFormSubmit(event) {
    event.preventDefault();
    Woolman.Collection.classList.add('processing');
    Woolman.Utils.prepareQueryParams();
    generateSearchParamsFromFormData(this.el);

    Woolman.ModalsAndDrawers.closeModalOrDrawer(this.closest('[data-drawer]').getAttribute('id'));
    getCollectionAJAX(Object.values(this.selectors));
  }

  async onInputChange(event) {
    Woolman.Collection.classList.add('processing');
    generateSearchParamsFromFormData(this.el);
    getCollectionAJAX(Object.values(this.selectors));
  }
}

// Sync checkboxes when drawer opens
const drawerFilter = document.getElementById('drawer-filter');
if (drawerFilter) {
  const observer = new MutationObserver((mutations) => {
    if (drawerFilter.classList.contains('is-open')) {
      // Ensure query params are up to date
      Woolman.Utils.prepareQueryParams();

      // Sync all checkboxes from URL params
      const drawerForm = drawerFilter.querySelector('facets-form form');
      if (drawerForm) {
        const drawerCheckboxes = drawerForm.querySelectorAll('input[type="checkbox"]');
        drawerCheckboxes.forEach(checkbox => {
          const paramName = checkbox.getAttribute('name');
          const value = checkbox.value;

          if (paramName && paramName.includes('filter') && Shopify.queryParams[paramName]) {
            const paramValues = Shopify.queryParams[paramName].split(',');
            checkbox.checked = paramValues.includes(value);
          }
        });
      }

      // Sync all forms to ensure consistency
      syncCheckboxStates();
    }
  });

  observer.observe(drawerFilter, {
    attributes: true,
    attributeFilter: ['class']
  });
}

if (customElements.get('facets-form') === undefined) {
  customElements.define('facets-form', FacetsForm);
}

class CurrentFacetButton extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.selectors = {
      grid: '#product-grid-ajax',
      count: '#products-count-ajax',
      currentFacets: '#current-facets-ajax',
      pagination: '#pagination-ajax',
    };

    this.btn = this.querySelector('a');
    this.btn.addEventListener('click', this.removeFacet.bind(this));
  }

  async removeFacet(event) {
    event.preventDefault();
    if (event.currentTarget.dataset.filterParamName === 'filter.v.price') {
      // Delete price filter params
      delete Shopify.queryParams['filter.v.price.lte'];
      delete Shopify.queryParams['filter.v.price.gte'];

      // Reset all price range sliders (sidebar and drawer)
      if (typeof PriceRangeSlider !== 'undefined' && PriceRangeSlider.resetAll) {
        PriceRangeSlider.resetAll();
      }

      // Remove data-updated attribute from all sliders
      const allSliders = document.querySelectorAll('price-range-slider');
      allSliders.forEach(slider => {
        slider.removeAttribute('data-updated');
      });

      getCollectionAJAX(Object.values(this.selectors));
    } else {
      const inputToReset = document.querySelector(`.filter-form [data-key-value="${ event.currentTarget.dataset.filterParamName }:${ event.currentTarget.dataset.filterValue }"]`);
      inputToReset.checked = false;
      inputToReset.dispatchEvent(new Event('change'));
    }
  }
}
if (customElements.get('current-facet-button') === undefined) {
  customElements.define('current-facet-button', CurrentFacetButton);
}

// On initial page load with an active price sort, take over and sort the whole
// result set by displayed price (Shopify only returns the manual order here).
if (document.getElementById('product-grid-ajax')) {
  applyGlobalPriceSort({ rangeOnly: !window.__priceSortActive });
}